import type { Contract } from '@gmn-portal/backend';
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Box, Building2, CircleDot, Eye, FileText, MoreHorizontal, Plus } from 'lucide-react';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useContracts, useCustomer, useDeleteContract, useObjects } from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { SearchCombobox } from '@/shared/components/inputs';
import { PageContainer, PageTitle, SimpleBreadcrumbItem } from '@/shared/components/layout';
import {
  SortableHeader,
  TableCard,
  TableCardContent,
  TableCardFooter,
  TableCardHeader,
  TableLoadingCell,
  TablePagination,
  TableSearchBar,
} from '@/shared/components/tables';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  EmptyTableRow,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderRow,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui';
import { TimelineControls } from '@/shared/components/ui/timeline-controls';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useTablePreferences } from '@/shared/services/table-preferences';
import { getSortParam } from '@/shared/utils/table-utils';

import { ContractsTimeline, type ContractsTimelineHandle } from '../components/contracts-timeline';
import { CopyContractModal } from '../components/copy-contract-modal';
import { ViewSelect } from '../components/view-select';

const CustomerCell = ({ customerId }: { customerId: string }) => {
  const { data: customer, isLoading } = useCustomer(customerId);
  if (isLoading) return <Skeleton className="h-4 w-24" />;
  return (
    <span className="text-muted-foreground truncate" title={customer?.name || '-'}>
      {customer?.name || '-'}
    </span>
  );
};

const ObjectCell = ({ objectIds }: { objectIds: string[] }) => {
  const { data: objectsData, isLoading } = useObjects({
    where: {
      id: {
        in: objectIds,
      },
    },
    limit: objectIds.length,
    disabled: !objectIds || objectIds.length === 0,
  });

  if (!objectIds || objectIds.length === 0) {
    return <span className="text-muted-foreground">-</span>;
  }

  if (isLoading) {
    return <Skeleton className="h-4 w-24" />;
  }

  const objects = objectsData?.docs || [];

  if (objects.length === 0) {
    return <span className="text-muted-foreground">-</span>;
  }

  if (objects.length === 1) {
    return (
      <span className="text-muted-foreground truncate" title={objects[0].name}>
        {objects[0].name}
      </span>
    );
  }

  // Show first 2 object names and count if more
  const displayObjects = objects.slice(0, 2);
  const remainingCount = objects.length - displayObjects.length;
  const allObjectNames = objects.map((obj) => obj.name);

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <span className="text-muted-foreground truncate">
          {displayObjects.map((obj) => obj.name).join(', ')}
          {remainingCount > 0 && ` +${remainingCount}`}
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="start" className="max-w-xs">
        <div className="text-sm">
          {allObjectNames.map((name, index) => (
            <div key={index}>{name}</div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

const ActionCell = ({
  contract,
  onDelete,
  onCopy,
  onViewDetail,
  basePath,
  isGmnAdmin,
}: {
  contract: Contract;
  onDelete: (contract: Contract) => void;
  onCopy: (contract: Contract) => void;
  onViewDetail: (id: string) => void;
  basePath: string;
  isGmnAdmin: boolean;
}) => {
  const navigate = useNavigate();
  const { t } = useIntl();

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions lint/a11y/useKeyWithClickEvents: Container only stops propagation, not interactive
    <div
      className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={() => onViewDetail(contract.id)}
        className="p-1.5 hover:bg-secondary text-primary rounded-md transition-colors"
        title={t('common.view')}
      >
        <Eye size={16} />
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="p-1.5 hover:bg-muted text-muted-foreground rounded-md transition-colors"
          >
            <MoreHorizontal size={16} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => navigate(`${basePath}/${contract.id}`)}>
            {t('common.edit')}
          </DropdownMenuItem>
          {isGmnAdmin && (
            <DropdownMenuItem onClick={() => onCopy(contract)}>
              {t('contracts.copyContract')}
            </DropdownMenuItem>
          )}
          {isGmnAdmin && (
            <DropdownMenuItem onClick={() => onDelete(contract)}>
              {t('common.delete')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export function ContractsOverview() {
  const { t } = useIntl();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isGmnAdmin = user?.role === 'gmn-admin';
  const basePath = '/gmn/contract-management/contracts';

  const tablePreferences = useTablePreferences({
    tableId: 'contracts-overview',
    defaultPreferences: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
      sorting: [],
      columnFilters: [],
      columnVisibility: {},
      view: 'table',
      searchTerm: '',
      selectedFilters: {
        customerId: undefined,
        objectId: undefined,
        status: undefined,
      },
    },
  });

  // Load initial preferences
  const preferences = tablePreferences.loadPreferences();

  const [searchTerm, setSearchTerm] = useState(preferences.searchTerm || '');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | undefined>(
    (preferences.selectedFilters?.customerId as string) || undefined
  );
  const [selectedObjectId, setSelectedObjectId] = useState<string | undefined>(
    (preferences.selectedFilters?.objectId as string) || undefined
  );
  const [selectedStatus, setSelectedStatus] = useState<'open' | 'closed' | undefined>(
    (preferences.selectedFilters?.status as 'open' | 'closed') || undefined
  );
  const [sorting, setSorting] = useState<SortingState>(preferences.sorting || []);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    preferences.columnFilters || []
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    preferences.columnVisibility || {}
  );
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState(
    preferences.pagination || {
      pageIndex: 0,
      pageSize: 10,
    }
  );
  const [view, setView] = useState<'table' | 'timeline'>(
    (preferences.view as 'table' | 'timeline') || 'table'
  );

  // Save preferences when they change
  useEffect(() => {
    tablePreferences.savePreferences({
      pagination,
      sorting,
      columnFilters,
      columnVisibility,
      view,
      searchTerm,
      selectedFilters: {
        customerId: selectedCustomerId,
        objectId: selectedObjectId,
        status: selectedStatus,
      },
    });
  }, [
    pagination,
    sorting,
    columnFilters,
    columnVisibility,
    view,
    searchTerm,
    selectedCustomerId,
    selectedObjectId,
    selectedStatus,
  ]);

  const handleViewDetail = useCallback(
    (id: string) => {
      navigate(`${basePath}/${id}`);
    },
    [navigate, basePath]
  );

  const columns: ColumnDef<Contract>[] = [
    {
      accessorKey: 'title',
      header: ({ column }) => <SortableHeader column={column} title={t('contracts.title')} />,
      cell: ({ row }) => (
        <span className="font-medium text-foreground truncate" title={row.getValue('title')}>
          {row.getValue('title')}
        </span>
      ),
      size: 180,
      minSize: 150,
      maxSize: 250,
    },
    {
      accessorKey: 'contractNumber',
      header: ({ column }) => (
        <SortableHeader column={column} title={t('contracts.contractNumber')} />
      ),
      cell: ({ row }) => (
        <span className="text-muted-foreground" title={row.getValue('contractNumber')}>
          {row.getValue('contractNumber')}
        </span>
      ),
      size: 150,
      minSize: 120,
      maxSize: 200,
    },
    {
      accessorKey: 'customer',
      header: ({ column }) => <SortableHeader column={column} title={t('customers.customer')} />,
      cell: ({ row }) => <CustomerCell customerId={row.getValue('customer') as string} />,
      size: 200,
      minSize: 150,
      maxSize: 250,
    },
    {
      accessorKey: 'aggregatedObjects',
      header: () => <div className="text-left font-medium">{t('objects.objects')}</div>,
      cell: ({ row }) => {
        const aggregatedObjects = row.getValue('aggregatedObjects') as (string | { id: string })[];
        const objectIds =
          aggregatedObjects?.map((obj) => (typeof obj === 'string' ? obj : obj.id)) || [];
        return <ObjectCell objectIds={objectIds} />;
      },
      size: 200,
      minSize: 150,
      maxSize: 250,
    },
    {
      accessorKey: 'startDate',
      header: ({ column }) => <SortableHeader column={column} title={t('contracts.startDate')} />,
      cell: ({ row }) => {
        const date = row.getValue('startDate') as string;
        return (
          <span className="text-muted-foreground">
            {date ? DateTime.fromISO(date).toFormat('dd.MM.yyyy') : '-'}
          </span>
        );
      },
      size: 130,
      minSize: 100,
      maxSize: 150,
    },
    {
      accessorKey: 'endDate',
      header: ({ column }) => <SortableHeader column={column} title={t('contracts.endDate')} />,
      cell: ({ row }) => {
        const date = row.getValue('endDate') as string;
        return (
          <span className="text-muted-foreground">
            {date ? DateTime.fromISO(date).toFormat('dd.MM.yyyy') : '-'}
          </span>
        );
      },
      size: 130,
      minSize: 100,
      maxSize: 150,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <SortableHeader column={column} title={t('contracts.statusLabel')} />,
      cell: ({ row }) => {
        const status = row.getValue('status') as 'open' | 'closed';
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
              status === 'closed'
                ? 'bg-muted text-muted-foreground border-border'
                : 'bg-success-subtle text-success-subtle-foreground border-transparent'
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                status === 'closed' ? 'bg-muted-foreground' : 'bg-success-subtle-foreground'
              }`}
            />
            {status === 'closed' ? t('contracts.status.closed') : t('contracts.status.open')}
          </span>
        );
      },
      size: 120,
      minSize: 100,
      maxSize: 150,
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => <SortableHeader column={column} title={t('common.createdAt')} />,
      cell: ({ row }) => {
        const date = row.getValue('createdAt') as string;
        return (
          <span className="text-muted-foreground">
            {DateTime.fromISO(date).toFormat('dd.MM.yyyy')}
          </span>
        );
      },
      size: 130,
      minSize: 100,
      maxSize: 150,
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">{t('common.actions')}</span>,
      cell: ({ row }) => (
        <ActionCell
          contract={row.original}
          onDelete={setContractToDelete}
          onCopy={setContractToCopy}
          onViewDetail={handleViewDetail}
          basePath={basePath}
          isGmnAdmin={!!isGmnAdmin}
        />
      ),
      size: 80,
      minSize: 60,
      maxSize: 100,
    },
  ];

  const { data: contractsData, isLoading } = useContracts({
    page: view === 'table' ? pagination.pageIndex + 1 : 1,
    limit: view === 'table' ? pagination.pageSize : 0,
    status: selectedStatus,
    where: {
      and: [
        ...(debouncedSearchTerm
          ? [
              {
                or: [
                  {
                    contractNumber: {
                      like: debouncedSearchTerm,
                    },
                  },
                  {
                    title: {
                      like: debouncedSearchTerm,
                    },
                  },
                ],
              },
            ]
          : []),
        ...(selectedCustomerId
          ? [
              {
                customer: {
                  equals: selectedCustomerId,
                },
              },
            ]
          : []),
        ...(selectedObjectId
          ? [
              {
                aggregatedObjects: {
                  in: [selectedObjectId],
                },
              },
            ]
          : []),
      ],
    },
    sort: getSortParam<Contract>(sorting, {
      customer: 'customer.name',
    }),
  });

  const [contractToDelete, setContractToDelete] = useState<Contract | null>(null);
  const [contractToCopy, setContractToCopy] = useState<Contract | null>(null);
  const deleteContract = useDeleteContract();
  const totalDocs = contractsData?.totalDocs || 0;

  const handleDeleteContract = async () => {
    if (contractToDelete) {
      try {
        await deleteContract.mutateAsync(contractToDelete.id);
        toast.success(t('contracts.contractDeletedSuccessfully'));
        setContractToDelete(null);
      } catch (_error) {
        toast.error(t('contracts.errorDeletingContract'));
      }
    }
  };

  const table = useReactTable({
    data: contractsData?.docs ?? [],
    columns,
    pageCount: contractsData?.totalPages ?? 0,
    rowCount: totalDocs,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
    },
    onPaginationChange: setPagination,
    manualPagination: true,
    manualSorting: true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columnResizeMode: 'onChange',
  });

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const timelineRef = useRef<ContractsTimelineHandle>(null);

  // Filter actions for TableCardHeader
  const filterActions = (
    <div className="flex items-center gap-3">
      {view === 'timeline' && (
        <TimelineControls
          onExpandAll={() => timelineRef.current?.handleExpandAll()}
          onCollapseAll={() => timelineRef.current?.handleCollapseAll()}
          onZoomIn={() => timelineRef.current?.handleZoomIn()}
          onZoomOut={() => timelineRef.current?.handleZoomOut()}
        />
      )}
      <ViewSelect view={view} onViewChange={(value) => setView(value)} />

      {/* Status Filter */}
      <Select
        value={selectedStatus || 'all'}
        onValueChange={(value) => {
          setSelectedStatus(value === 'all' ? undefined : (value as 'open' | 'closed'));
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
      >
        <SelectTrigger className="h-9 min-w-[140px] w-auto">
          <div className="flex items-center">
            <CircleDot className="size-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder={t('contracts.statusFilter')} />
          </div>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('common.all')}</SelectItem>
          <SelectItem value="open">{t('contracts.status.open')}</SelectItem>
          <SelectItem value="closed">{t('contracts.status.closed')}</SelectItem>
        </SelectContent>
      </Select>

      <SearchCombobox
        value={selectedCustomerId}
        onSelect={(value) => {
          setSelectedCustomerId(value || undefined);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
        collectionKey="customers"
        searchKey="name"
        className="h-9 min-w-[180px] w-auto"
        texts={{ selectItemMsg: t('customers.customer') }}
        icon={Building2}
      />
      <SearchCombobox
        value={selectedObjectId}
        onSelect={(value) => {
          setSelectedObjectId(value || undefined);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
        collectionKey="objects"
        searchKey="name"
        className="h-9 min-w-[180px] w-auto"
        texts={{ selectItemMsg: `${t('objects.object')}` }}
        icon={Box}
      />
    </div>
  );

  return (
    <PageContainer>
      <TooltipProvider>
        <PageTitle
          icon={FileText}
          breadcrumb={
            <>
              <SimpleBreadcrumbItem>{t('contracts.contracts')}</SimpleBreadcrumbItem>
              <SimpleBreadcrumbItem isLast>
                {t('navigation.items.allContracts')}
              </SimpleBreadcrumbItem>
            </>
          }
          title={t('contracts.manageContracts')}
          primaryAction={{
            label: t('contracts.addContract'),
            onClick: () => navigate(`${basePath}/new`),
            icon: <Plus size={16} />,
            disabled: !isGmnAdmin,
          }}
        />

        <TableCard className="mb-6">
          <TableCardHeader>
            <TableSearchBar
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              placeholder={t('contracts.searchPlaceholder')}
              actions={filterActions}
            />
          </TableCardHeader>

          {view === 'table' ? (
            <>
              <TableCardContent>
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableHeaderRow key={headerGroup.id}>
                        {headerGroup.headers.map((header, index) => (
                          <TableHead
                            key={header.id}
                            style={{ width: `${header.getSize()}px` }}
                            className={
                              index === 0
                                ? 'pl-6'
                                : index === headerGroup.headers.length - 1
                                  ? 'pr-6 text-right'
                                  : ''
                            }
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </TableHead>
                        ))}
                      </TableHeaderRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableLoadingCell colSpan={columns.length} />
                    ) : table.getRowModel().rows?.length ? (
                      table.getRowModel().rows.map((row) => (
                        <TableRow
                          key={row.id}
                          data-state={row.getIsSelected() && 'selected'}
                          className="cursor-pointer"
                          onClick={() => handleViewDetail(row.original.id)}
                        >
                          {row.getVisibleCells().map((cell, index) => (
                            <TableCell
                              key={cell.id}
                              style={{ width: `${cell.column.getSize()}px` }}
                              className={
                                index === 0
                                  ? 'pl-6'
                                  : index === row.getVisibleCells().length - 1
                                    ? 'pr-6'
                                    : ''
                              }
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <EmptyTableRow colSpan={columns.length} />
                    )}
                  </TableBody>
                </Table>
              </TableCardContent>

              <TableCardFooter>
                <TablePagination table={table} />
              </TableCardFooter>
            </>
          ) : (
            <div className="p-4">
              {contractsData?.docs && contractsData?.docs.length > 0 ? (
                <ContractsTimeline
                  ref={timelineRef}
                  contracts={contractsData?.docs || []}
                  groupByCustomer
                  onDelete={isGmnAdmin ? setContractToDelete : undefined}
                  onCopy={isGmnAdmin ? setContractToCopy : undefined}
                />
              ) : (
                <div className="p-8 text-center text-muted-foreground text-sm">
                  {t('common.noResults')}
                </div>
              )}
            </div>
          )}
        </TableCard>

        <AlertDialog
          open={!!contractToDelete}
          onOpenChange={(open) => !open && setContractToDelete(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('contracts.deleteContract')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('contracts.deleteContractConfirmation', {
                  name: contractToDelete?.contractNumber || '',
                })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteContract}>
                {t('common.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <CopyContractModal
          contract={contractToCopy}
          open={!!contractToCopy}
          onOpenChange={(open) => !open && setContractToCopy(null)}
        />
      </TooltipProvider>
    </PageContainer>
  );
}
